import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { recipesAPI, salesAPI } from '../lib/api';
import { formatCurrency, formatNumber, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash, Receipt, X } from '@phosphor-icons/react';

export default function Sales() {
  const [recipes, setRecipes] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, salesRes] = await Promise.all([
        recipesAPI.getAll(),
        salesAPI.getAll(20),
      ]);
      setRecipes(recipesRes.data);
      setRecentSales(salesRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (recipe) => {
    const existing = cart.find(item => item.recipe_id === recipe.id);
    if (existing) {
      setCart(cart.map(item =>
        item.recipe_id === recipe.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        recipe_id: recipe.id,
        recipe_name: recipe.name,
        quantity: 1,
        unit_price: recipe.selling_price,
        cost: recipe.total_cost,
      }]);
    }
  };

  const updateQuantity = (recipeId, delta) => {
    setCart(cart.map(item => {
      if (item.recipe_id === recipeId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (recipeId) => {
    setCart(cart.filter(item => item.recipe_id !== recipeId));
  };

  const clearCart = () => {
    setCart([]);
    setNotes('');
  };

  const calculateTotals = () => {
    const totalRevenue = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const totalCost = cart.reduce((sum, item) => sum + (item.cost * item.quantity), 0);
    const profit = totalRevenue - totalCost;
    return { totalRevenue, totalCost, profit };
  };

  const handleSubmitSale = async () => {
    if (cart.length === 0) {
      toast.error('Sepet boş');
      return;
    }

    setSubmitting(true);
    try {
      await salesAPI.create({
        items: cart.map(item => ({
          recipe_id: item.recipe_id,
          quantity: item.quantity,
        })),
        notes: notes || undefined,
      });
      
      toast.success('Satış kaydedildi');
      clearCart();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Satış kaydedilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRecipes = recipes.filter(r =>
    categoryFilter === 'all' || r.category === categoryFilter
  );

  const { totalRevenue, totalCost, profit } = calculateTotals();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
            Satış
          </h1>
          <p className="mt-2 text-muted-foreground">
            Menü ürünlerini satın ve stok otomatik güncellensin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu Items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-4">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 h-11" data-testid="sales-category-filter">
                  <SelectValue placeholder="Kategori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Menü</SelectItem>
                  <SelectItem value="ana_yemek">Ana Yemek</SelectItem>
                  <SelectItem value="baslangic">Başlangıç</SelectItem>
                  <SelectItem value="tatli">Tatlı</SelectItem>
                  <SelectItem value="icecekler">İçecekler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredRecipes.length === 0 ? (
                <div className="col-span-full text-center py-12 border border-border bg-card rounded-xl">
                  <Receipt size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Menüde ürün bulunmuyor</p>
                </div>
              ) : (
                filteredRecipes.map((recipe) => {
                  const inCart = cart.find(item => item.recipe_id === recipe.id);
                  return (
                    <button
                      key={recipe.id}
                      onClick={() => addToCart(recipe)}
                      className={`p-4 border rounded-xl text-left transition-colors ${
                        inCart
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-card hover:bg-muted/50'
                      }`}
                      data-testid={`menu-item-${recipe.id}`}
                    >
                      <span className="text-xs text-muted-foreground">
                        {getCategoryLabel(recipe.category)}
                      </span>
                      <h3 className="font-medium mt-1 line-clamp-1">{recipe.name}</h3>
                      <p className="font-data font-semibold text-primary mt-2">
                        {formatCurrency(recipe.selling_price)}
                      </p>
                      {inCart && (
                        <span className="inline-block mt-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full">
                          {inCart.quantity} adet
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Cart */}
          <div className="border border-border bg-card rounded-xl p-6 h-fit sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShoppingCart size={24} weight="duotone" className="text-primary" />
                <h2 className="font-heading text-xl font-semibold">Sepet</h2>
              </div>
              {cart.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCart}
                  className="text-muted-foreground"
                >
                  Temizle
                </Button>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart size={40} weight="duotone" className="mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground text-sm">Sepet boş</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-64 overflow-auto mb-4">
                  {cart.map((item) => (
                    <div
                      key={item.recipe_id}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                      data-testid={`cart-item-${item.recipe_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.recipe_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(item.unit_price)} x {item.quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.recipe_id, -1)}
                        >
                          <Minus size={14} />
                        </Button>
                        <span className="font-data w-6 text-center">{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.recipe_id, 1)}
                        >
                          <Plus size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => removeFromCart(item.recipe_id)}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 py-4 border-y border-border">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Toplam</span>
                    <span className="font-data font-semibold">{formatCurrency(totalRevenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Maliyet</span>
                    <span className="font-data">{formatCurrency(totalCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Kar</span>
                    <span className={`font-data font-semibold ${profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                      {formatCurrency(profit)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 mt-4">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Satış notu (opsiyonel)"
                    className="resize-none"
                    rows={2}
                    data-testid="sale-notes"
                  />
                  <Button
                    onClick={handleSubmitSale}
                    disabled={submitting}
                    className="w-full h-12 bg-primary hover:bg-primary/90"
                    data-testid="complete-sale-button"
                  >
                    {submitting ? 'Kaydediliyor...' : 'Satışı Tamamla'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Sales */}
        <div className="border border-border bg-card p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4">Son Satışlar</h2>
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-b border-border">
                <TableHead className="py-3">Tarih</TableHead>
                <TableHead>Ürünler</TableHead>
                <TableHead className="text-right">Toplam</TableHead>
                <TableHead className="text-right">Kar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <Receipt size={40} weight="duotone" className="mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground text-sm">Henüz satış yapılmamış</p>
                  </TableCell>
                </TableRow>
              ) : (
                recentSales.map((sale) => (
                  <TableRow key={sale.id} className="table-row-hover border-b border-border/40">
                    <TableCell className="py-3 text-sm">
                      {new Date(sale.created_at).toLocaleString('tr-TR')}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {sale.items.map((item, idx) => (
                          <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                            {item.recipe_name} x{item.quantity}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-data">
                      {formatCurrency(sale.total_revenue)}
                    </TableCell>
                    <TableCell className={`text-right font-data ${sale.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                      {formatCurrency(sale.profit)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
